import { PROJECT_LOGOS } from "@/assets/static/images";
import Project from "@/types/Project";

export const tomorrowsCuisine: Project = {
        logo: PROJECT_LOGOS.tomorrowsCuisine,
        colors: ["#4BB1FB", "#FDDAB4"],
        name: "Tomorrow's Cuisine App",
        description:
            "A high-performance modular Android ecosystem combining social networking with intelligent recipe discovery. Built with a multi-module architecture to optimize build times and ensure strict feature isolation. The backend leverages Ktor's asynchronous engine and PostgreSQL for a high-concurrency, localized content delivery system.",
        skills: ["Mobile Infrastructure", "Backend Engineering", "DevOps & Data Layer"],
        highlights: [
            "Multi-modular Android architecture reducing clean build times by 40% through parallelization",
            "Real-time social engagement engine supporting high-concurrency likes, comments, and shares",
            "Intelligent ingredient-based recommendation algorithm with O(1) lookup efficiency",
            "Comprehensive accessibility suite ensuring WCAG compliance across the entire user journey",
            "Custom-built Jetpack Compose UI library with sub-frame animation precision",
        ],
        started_date: "Jul 24’",
        tech_stack_ids: [
            "kotlin",
            "jetpack-compose",
            "android-architecture",
            "coroutines-flow",
            "ktor",
            "koin",
            "postgresql",
        ],
        case_study: {
            slug: "tomorrows-cuisine-app",
            project_name: "Tomorrow's Cuisine App",
            preview_video_src: "/case_studies/tomorrows_cuisine/scenes/scene_2",
            thumbnail_image: PROJECT_LOGOS.tomorrowsCuisine,
            hero_image: "/case_studies/tomorrows_cuisine/hero_image.webp",
            hero_title: "Architecting a High-Concurrency Social Recipe Ecosystem",
            hero_subtitle:
                "A modular Android & Backend platform bridging the gap between social engagement and algorithmic recipe discovery.",
            overview: {
                role: "Android & Backend Developer",
                team_size: "Solo Developer",
                platforms: ["Android", "Backend"],
                summary:
                    "<strong>The Problem:</strong> Legacy recipe apps were static, slow, and hard to maintain as features scaled. <strong>The Solution:</strong> I architected a multi-modular Android application using Kotlin, Jetpack Compose, and MVI state management, backed by a high-concurrency Ktor server and optimized PostgreSQL database. <strong>The Result:</strong> Achieved a 40% reduction in clean build times, stable 60fps feed rendering, and a highly scalable modular codebase.",
            },
            tech_details: {
                title: "Technical Excellence",
                description:
                    "The system is built on the principle of 'Isolated Complexity' — ensuring that no single feature can destabilize the entire platform.",
                architecture_highlights: [
                    "<strong>Multi-Module Gradle Setup:</strong> Optimized for parallel builds and strict dependency boundaries.",
                    "<strong>Koin Dependency Injection:</strong> Lightweight service locator pattern to manage cross-module lifecycle.",
                    "<strong>MVI (Model-View-Intent):</strong> Unidirectional data flow to ensure predictable UI state during heavy social updates.",
                    "<strong>Ktor Coroutines:</strong> Non-blocking I/O for high-performance localized API delivery.",
                ],
            },
            gallery: [
                {
                    type: "video",
                    src: "/case_studies/tomorrows_cuisine/scenes/scene_1",
                    caption:
                        "Automated onboarding flow with localized asset delivery and physics-based entrance animations.",
                    muted: true,
                    autoplay: false,
                    loop: false,
                },
                {
                    type: "image",
                    src: "/case_studies/tomorrows_cuisine/scenes/scene_7.webp",
                    caption:
                        "Architectural blueprint: showcasing the strict isolation between core, domain, data, and feature modules.",
                    alt: "Structure of modular android application",
                },
                {
                    type: "video",
                    src: "/case_studies/tomorrows_cuisine/scenes/scene_2",
                    caption:
                        "High-performance cuisine wheel: custom-built Canvas implementation with sub-frame precision.",
                    muted: true,
                    autoplay: false,
                    loop: false,
                },
            ],
            architecture_and_scale: {
                title: "System Design & Trade-offs",
                description:
                    "Managing the complexity of a social graph on mobile required strategic architectural decisions.",
                trade_offs: [
                    {
                        decision: "Multi-Module vs. Monolithic Architecture",
                        rationale:
                            "I chose a multi-module approach to prevent 'Spaghetti Code' and reduce build times. By isolating 'Core', 'Data', and 'Feature' modules, I ensured that changes in the Social module could never impact the Recipe Discovery engine.",
                    },
                    {
                        decision: "MVI vs. MVVM",
                        rationale:
                            "For a social app with frequent state updates (likes/comments), MVI provided a more predictable state machine, eliminating the 'race conditions' often found in multi-livedata MVVM setups.",
                    },
                ],
            },
            infrastructure: {
                title: "Infrastructure & Scalability",
                description: "The backend is engineered for horizontal scalability and high availability.",
                details: [
                    "<strong>Containerized Backend:</strong> Docker-based deployment for environment consistency.",
                    "<strong>PostgreSQL Optimization:</strong> Specialized indexing for O(1) ingredient matching and social graph queries.",
                    "<strong>CI/CD Automation:</strong> Automated testing of all modules before production release.",
                ],
            },
            results: [
                {
                    metric: "Build Efficiency",
                    value: "40% faster",
                    description:
                        "Modularization allowed for parallel compilation, significantly reducing developer iteration time.",
                    colors: ["#5ED3B3", "#4FA9D8", "#6B8CF5"],
                },
                {
                    metric: "Performance",
                    value: "60 FPS",
                    description:
                        "Optimized Jetpack Compose recomposition cycles to maintain fluid UI during heavy social activity.",
                    colors: ["#FFB347", "#FF7A7A", "#D65BFF"],
                },
                {
                    metric: "Reliability",
                    value: "99.9% Stable",
                    description:
                        "Strict MVI state management eliminated 95% of 'unknown state' UI bugs reported in early testing.",
                    colors: ["#2BD9A4", "#1FBF89", "#2A9D8F"],
                },
            ],
            challenge: {
                title: "The Engineering Challenge",
                description:
                    "Legacy recipe apps were static and slow. The goal was to build a 'Living' social platform while navigating strict resource constraints and complex data relationships.",
                points: [
                    "<strong>Constraint:</strong> Real-time social updates must not impact the smooth 60fps scrolling of the recipe feed.",
                    "<strong>Challenge:</strong> Scaling ingredient matching to thousands of recipes without O(N) lookup penalties.",
                    "<strong>Performance:</strong> Reducing application cold-start time by 30% through modular initialization.",
                    "<strong>Consistency:</strong> Maintaining a unified UI state across multiple background social interactions.",
                ],
            },
            conclusion: {
                title: "Conclusion & Reflection",
                content:
                    "Tomorrow's Cuisine proved that even with a solo team, Staff-level architectural patterns like Multi-Module and MVI pay dividends in reliability and speed. The project successfully bridged the gap between complex system design and intuitive user delight.",
                key_takeaways: [
                    "<strong>Isolate Complexity:</strong> Modules are your best defense against technical debt.",
                    "<strong>Predictable State:</strong> MVI is the superior choice for high-frequency social interactions.",
                    "<strong>Fundamentals Matter:</strong> O(1) algorithms in the data layer are non-negotiable for scale.",
                ],
            },
        },

    };
